import React from "react";
import { ConfigProvider, App as AntdApp } from "antd";
import ruRU from "antd/locale/ru_RU";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { RefineThemes, useNotificationProvider } from "@refinedev/antd";
import { Refine, Authenticated, ErrorComponent } from "@refinedev/core";
import routerProvider, { CatchAllNavigate, NavigateToResource, UnsavedChangesNotifier } from "@refinedev/react-router-v6";
import { authProvider } from "@/providers/auth";
import { dataProvider } from "@/providers/data";
import { TaskTrackerProvider } from "@/providers/task-tracker";

const i18nProvider = {
  translate: (key: string) => {
    const translations: Record<string, string> = {
      "warnWhenUnsavedChanges": "Вы уверены, что хотите уйти? У вас есть несохраненные изменения.",
      "buttons.create": "Создать",
      "buttons.save": "Сохранить",
      "buttons.cancel": "Отмена",
      "buttons.delete": "Удалить",
      "buttons.edit": "Редактировать",
      "reviews.titles.list": "Отзывы",
    };
    return translations[key] || key;
  },
  changeLocale: () => Promise.resolve(),
  getLocale: () => "ru",
};
import ReviewsList from "./routes/reviews/list";
import ReviewShow from "./routes/reviews/show";
import StrategiesList from "./routes/strategies/list";
import StrategyCreate from "./routes/strategies/create";
import StrategyEdit from "./routes/strategies/edit";
import TemplatesList from "./routes/templates/list";
import TemplateCreate from "./routes/templates/create";
import TemplateEdit from "./routes/templates/edit";
import KBList from "./routes/kb/list";
import { KBCreatePage } from "./routes/kb/create";
import { KBEditPage } from "./routes/kb/edit";
import StopWordsList from "./routes/stopwords/list";
import { StopWordCreatePage } from "./routes/stopwords/create";
import { StopWordEditPage } from "./routes/stopwords/edit";
import ResponseSettingsPage from "./routes/settings/response";
import AIStylesList from "./routes/settings/ai-styles/list";
import { AIStyleCreatePage } from "./routes/settings/ai-styles/create";
import { AIStyleEditPage } from "./routes/settings/ai-styles/edit";
import Layout from "./components/layout";
import Dashboard from "./routes/dashboard/index";
import LoginPage from "./routes/login";
import { resources } from "./config/resources";
import "@refinedev/antd/dist/reset.css";

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ConfigProvider theme={RefineThemes.Blue} locale={ruRU}>
        <AntdApp>
        <Refine
          routerProvider={routerProvider}
          dataProvider={dataProvider}
          authProvider={authProvider}
          notificationProvider={useNotificationProvider()}
          i18nProvider={i18nProvider}
          options={{ syncWithLocation: true, warnWhenUnsavedChanges: true }}
          resources={resources}
        >
          <Routes>
            <Route element={ (
              <Authenticated key="authenticated-layout" fallback={<CatchAllNavigate to="/login" />}> <TaskTrackerProvider><Layout><Outlet/></Layout></TaskTrackerProvider> </Authenticated>
            )}>
              <Route index element={<Dashboard />} />
              <Route path="/reviews" element={<ReviewsList />} />
              <Route path="/reviews/:id" element={<ReviewShow />} />
              <Route path="/strategies" element={<StrategiesList />} />
              <Route path="/strategies/create" element={<StrategyCreate />} />
              <Route path="/strategies/:id" element={<StrategyEdit />} />
              <Route path="/templates" element={<TemplatesList />} />
              <Route path="/templates/create" element={<TemplateCreate />} />
              <Route path="/templates/edit/:id" element={<TemplateEdit />} />
              <Route path="/kb" element={<KBList />} />
              <Route path="/kb/create" element={<KBCreatePage />} />
              <Route path="/kb/edit/:id" element={<KBEditPage />} />
              <Route path="/stop-words" element={<StopWordsList />} />
              <Route path="/stop-words/create" element={<StopWordCreatePage />} />
              <Route path="/stop-words/edit/:id" element={<StopWordEditPage />} />
              <Route path="/settings/response" element={<ResponseSettingsPage />} />
              <Route path="/settings/ai-styles" element={<AIStylesList />} />
              <Route path="/settings/ai-styles/create" element={<AIStyleCreatePage />} />
              <Route path="/settings/ai-styles/edit/:id" element={<AIStyleEditPage />} />
              <Route path="*" element={<ErrorComponent />} />
            </Route>
            <Route element={(<Authenticated key="authenticated-auth" fallback={<Outlet />}><NavigateToResource resource="dashboard" /></Authenticated>)}>
              <Route path="/login" element={<LoginPage />} />
            </Route>
          </Routes>
          <UnsavedChangesNotifier />
        </Refine>
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  );
};

export default App;
